import { Info } from "lucide-react";
import React, { useEffect } from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Event } from "@/lib/types";

interface CartSummaryProps {
  vipSeats: string[];
  vipTicketCount?: number;
  standingTickets: number;
  earlyBirdTickets: number;
  vipPrice: number;
  standingPrice: number;
  earlyBirdPrice: number;
  discountPercentage?: number;
  event: Event;
  onTotalChange?: (total: number) => void;
}

export const CartSummary: React.FC<CartSummaryProps> = ({
  vipSeats,
  vipTicketCount = 0,
  standingTickets,
  earlyBirdTickets,
  vipPrice,
  standingPrice,
  earlyBirdPrice,
  discountPercentage = 0,
  event,
  onTotalChange,
}) => {
  // Calculate VIP quantity based on template type
  const vipQuantity = event.templateType === 'no_seating'
    ? vipSeats.length
    : vipTicketCount;

  // Base ticket totals (these prices are already tax-inclusive)
  const vipTotal = vipQuantity * vipPrice;
  const standingTotal = standingTickets * standingPrice;
  const earlyBirdTotal = earlyBirdTickets * earlyBirdPrice;
  const subtotal = vipTotal + standingTotal + earlyBirdTotal;

  // GST amount (extracted from tax-inclusive prices)
  const inclusiveGstRate = parseFloat(event.inclusiveGstRate) || 0;
  const displayTaxLineItem = parseFloat(event.displaytaxLineitem) || 0;

  // Calculate GST component by dividing the subtotal by the GST rate
  const gstAmount = inclusiveGstRate > 0
    ? subtotal / inclusiveGstRate
    : 0;

  // Discount calculation (applied to tax-inclusive amount)
  const discountAmount = (subtotal * discountPercentage) / 100;
  const totalAfterDiscount = subtotal - discountAmount;

  // Calculate Slay fee
  const slayFeePercentage = parseFloat(event.slayFeePercentage) || 0;
  const slayFeeFixed = parseFloat(event.slayFeeFixed) || 0;
  const slayFeeAmount = (totalAfterDiscount * slayFeePercentage / 100) + slayFeeFixed;

  // Final total (prices are already tax-inclusive)
  const total = totalAfterDiscount + slayFeeAmount;

  // Notify parent component of total change
  useEffect(() => {
    console.log('Cart total updated:', {
      subtotal,
      discountAmount,
      totalAfterDiscount,
      slayFeeAmount,
      finalTotal: total,
      currency: event.currency || 'AUD'
    });
    onTotalChange?.(total);
  }, [total, onTotalChange, subtotal, discountAmount, totalAfterDiscount, slayFeeAmount, event.currency]);

  return (
    <div className="bg-gray-800/50 rounded-lg p-6 space-y-4">
      <h3 className="text-xl font-bold text-white">Order Summary</h3>

      {vipQuantity > 0 && (
        <div className="flex justify-between text-gray-200">
          <span>
            VIP Tickets ({vipQuantity})
            {event.templateType === 'no_seating' && vipSeats.length > 0 &&
              ` - Seats: ${vipSeats.join(", ")}`
            }
          </span>
          <span>${vipTotal.toFixed(2)}</span>
        </div>
      )}

      {standingTickets > 0 && (
        <div className="flex justify-between text-gray-200">
          <span>General Admission ({standingTickets})</span>
          <span>${standingTotal.toFixed(2)}</span>
        </div>
      )}

      {earlyBirdTickets > 0 && (
        <div className="flex justify-between text-gray-200">
          <span>Early Bird ({earlyBirdTickets})</span>
          <span>${earlyBirdTotal.toFixed(2)}</span>
        </div>
      )}

      <div className="flex justify-between text-gray-200">
        <span>Subtotal</span>
        <span>${subtotal.toFixed(2)}</span>
      </div>

      {displayTaxLineItem > 0 && inclusiveGstRate > 0 && (
        <div className="flex justify-between text-gray-200">
          <span>Includes GST ({displayTaxLineItem}%)</span>
          <span>${gstAmount.toFixed(2)}</span>
        </div>
      )}

      {discountPercentage > 0 && (
        <div className="flex justify-between text-green-400">
          <span>Discount ({discountPercentage}%)</span>
          <span>-${discountAmount.toFixed(2)}</span>
        </div>
      )}

      {slayFeeAmount > 0 ? (
        <div className="flex justify-between text-gray-200">
          <span>Slay it forward fee</span>
          <span>${slayFeeAmount.toFixed(2)}</span>
        </div>
      ) : null}

      <div className="border-t border-gray-600 pt-4">
        <div className="flex justify-between text-white font-bold">
          <span>Total</span>
          <span>${total.toFixed(2)}</span>
        </div>
        {slayFeeAmount === 0 && (
          <div className="text-[#9b87f5] text-sm mt-2 italic">
            Free booking fees! {event.producerName} has covered it for you 🎉
          </div>
        )}
      </div>
    </div>
  );
};